import { ReactNode } from 'react';
import '../assets/normalize.css';
import 'react-loading-skeleton/dist/skeleton.css';
import { SkeletonTheme } from 'react-loading-skeleton';
import { Helmet } from 'react-helmet';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider } from 'styled-components';
import {
  DesignTokenLightMode,
  DesignTokenDarkMode,
} from '@versita/frontend/design-tokens';
import { GlobalStyles } from './globalStyles';
import useMediaQuery from '../utils/useMediaQuery/useMediaQuery';

export enum ThemeStyle {
  Light = 'light',
  Dark = 'dark',
}
type ThemeProps = {
  children: ReactNode;
  style?: ThemeStyle;
  theme?: Record<string, unknown>;
};

function getTokensForTheme(style: ThemeStyle) {
  switch (style) {
    case ThemeStyle.Dark:
      return DesignTokenDarkMode;

    case ThemeStyle.Light:
    default:
      return DesignTokenLightMode;
  }
}

export const Theme = ({
  children,
  style,
  theme: themeOverrides = {},
}: ThemeProps) => {
  const isDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const defaultStyle = isDarkMode ? ThemeStyle.Dark : ThemeStyle.Light;
  const theme = {
    ...getTokensForTheme(style || defaultStyle),
    ...themeOverrides,
  };
  return (
    <ThemeProvider theme={theme}>
      <Toaster position="bottom-right" />
      <SkeletonTheme
        baseColor={theme.colors.neutrals.grey1}
        highlightColor={theme.colors.neutrals.grey3}
      >
        <Helmet>
          <style>
            {`
                @font-face{
                  font-family:"ReplicaPro";
                  src:url("/assets/fonts/ReplicaPro-Regular.eot");
                  src:url("/assets/fonts/ReplicaPro-Regular.eot") format("embedded-opentype"),url("/assets/fonts/ReplicaPro-Regular.ttf") format("truetype"),url("/assets/fonts/ReplicaPro-Regular.woff") format("woff");
                  font-display:swap;
                  font-weight: normal;
                  font-style: normal;
                }
                @font-face{
                  font-family:"ReplicaPro";
                  src:url("/assets/fonts/ReplicaPro-Light.eot");
                  src:url("/assets/fonts/ReplicaPro-Light.eot") format("embedded-opentype"),url("/assets/fonts/ReplicaPro-Light.ttf") format("truetype"),url("/assets/fonts/ReplicaPro-Light.woff") format("woff");
                  font-display:swap;
                  font-weight: 200;
                  font-style: normal;
                }
                @font-face{
                  font-family:"ReplicaPro";
                  src:url("/assets/fonts/ReplicaPro-Italic.eot");
                  src:url("/assets/fonts/ReplicaPro-Italic.eot") format("embedded-opentype"),url("/assets/fonts/ReplicaPro-Italic.ttf") format("truetype"),url("/assets/fonts/ReplicaPro-Italic.woff") format("woff");
                  font-display:swap;
                  font-weight: normal;
                  font-style: italic;
                }
                @font-face{
                  font-family:"ReplicaPro";
                  src:url("/assets/fonts/ReplicaPro-Bold.eot");
                  src:url("/assets/fonts/ReplicaPro-Bold.eot") format("embedded-opentype"),url("/assets/fonts/ReplicaPro-Bold.ttf") format("truetype"),url("/assets/fonts/ReplicaPro-Bold.woff") format("woff");
                  font-display:swap;
                  font-weight: bold;
                  font-style: normal;
                }
                @font-face{
                  font-family:"ReplicaPro";
                  src:url("/assets/fonts/ReplicaPro-Heavy.eot");
                  src:url("/assets/fonts/ReplicaPro-Heavy.eot") format("embedded-opentype"),url("/assets/fonts/ReplicaPro-Heavy.ttf") format("truetype"),url("/assets/fonts/ReplicaPro-Heavy.woff") format("woff");
                  font-display:swap;
                  font-weight: 700;
                  font-style: normal;
                }
              `}
          </style>
        </Helmet>
        <GlobalStyles />
        {children}
      </SkeletonTheme>
    </ThemeProvider>
  );
};
